<template>
  <div class="welcome-audio" :class="{ 'is-playing': playing }">
    <button class="welcome-play" type="button" :aria-label="`${playing ? 'Pause' : 'Play'} a short welcome in ${language === 'en' ? 'English' : 'Chinese'}`" :aria-pressed="playing" :aria-description="clip.text" @click="toggle">
      <svg class="welcome-orbit" viewBox="0 0 52 52" aria-hidden="true">
        <path class="welcome-orbit-line" d="M38 7C26 1 9 8 5 23S11 48 26 47 48 35 47 23 40 6 30 5" fill="none" />
        <circle class="welcome-progress" cx="26" cy="26" r="21" pathLength="1" :stroke-dasharray="`${progress} 1`" fill="none" />
        <g v-if="playing" fill="currentColor"><rect x="21" y="19" width="3" height="14" rx=".8" /><rect x="29" y="19" width="3" height="14" rx=".8" /></g>
        <path v-else d="M22 18.5 34 26 22 33.5Z" fill="currentColor" stroke="currentColor" stroke-linejoin="round" />
      </svg>
      <span class="welcome-title">{{ language === 'en' ? 'A little hello' : '一聲問候' }}</span>
    </button>
    <div class="welcome-languages" role="group" aria-label="Welcome language">
      <button type="button" :aria-pressed="language === 'en'" aria-label="English welcome" @click="choose('en')">EN</button>
      <button type="button" lang="zh-Hant" :aria-pressed="language === 'zh'" aria-label="中文問候" @click="choose('zh')">中</button>
    </div>
    <span class="welcome-status" role="status">{{ error }}</span>
    <audio ref="audio" :src="clip.src" preload="none" @loadedmetadata="updateTime" @timeupdate="updateTime" @play="onPlay" @pause="onPause" @ended="ended" @error="onError" />
  </div>
</template>

<script>
function localGreetingPeriod() {
  const hour = new Date().getHours();
  return hour >= 5 && hour < 12 ? 'morning' : hour >= 12 && hour < 18 ? 'afternoon' : 'evening';
}

const chineseClips = {
  morning: { src: '/assets/audio/welcome-zh-morning-v6.mp3', duration: 2.533813, text: '早安，歡迎訪問我的個人網站。' },
  afternoon: { src: '/assets/audio/welcome-zh-afternoon-v21.mp3', duration: 2.795000, text: '下午好，歡迎訪問我的個人網站。' },
  evening: { src: '/assets/audio/welcome-zh-evening.mp3', duration: 2.612125, text: '晚上好，歡迎訪問我的個人網站。' },
};

const clips = {
  en: { src: '/assets/audio/welcome-en.mp3', duration: 4.153375, text: "Hi, I'm Henry, welcome to my little corner of the internet." },
};

export default {
  name: 'HomeWelcomeAudio',
  props: { active: { type: Boolean, default: true } },
  emits: ['play'],
  data: () => ({ language: 'en', greetingPeriod: localGreetingPeriod(), playing: false, currentTime: 0, duration: clips.en.duration, error: '' }),
  computed: {
    clip() { return this.language === 'zh' ? chineseClips[this.greetingPeriod] : clips.en; },
    progress() { return this.duration ? this.currentTime / this.duration : 0; },
  },
  watch: { active(value) { if (!value) this.$refs.audio?.pause(); } },
  mounted() {
    this._otherAudio = event => { if (event.detail !== this) this.$refs.audio?.pause(); };
    this._visibility = () => { if (document.hidden) this.$refs.audio?.pause(); };
    window.addEventListener('view-summary-play', this._otherAudio);
    document.addEventListener('visibilitychange', this._visibility);
  },
  beforeUnmount() {
    this.$refs.audio?.pause();
    window.removeEventListener('view-summary-play', this._otherAudio);
    document.removeEventListener('visibilitychange', this._visibility);
  },
  methods: {
    async toggle() {
      const audio = this.$refs.audio;
      if (!audio || !this.active) return;
      if (!audio.paused) { audio.pause(); return; }
      this.error = '';
      if (this.refreshGreeting()) await this.$nextTick();
      if (!this.active) return;
      if (audio.ended) audio.currentTime = 0;
      try { await audio.play(); } catch (error) {
        if (error.name !== 'AbortError') this.onError();
      }
    },
    refreshGreeting() {
      const period = localGreetingPeriod();
      if (this.language !== 'zh' || period === this.greetingPeriod) return false;
      this.greetingPeriod = period;
      this.currentTime = 0;
      this.duration = this.clip.duration;
      return true;
    },
    async choose(language) {
      if (language === this.language) return;
      const resume = this.playing;
      this.$refs.audio.pause();
      this.language = language;
      this.refreshGreeting();
      this.currentTime = 0;
      this.duration = this.clip.duration;
      this.error = '';
      await this.$nextTick();
      if (resume && this.language === language) this.toggle();
    },
    onPlay() {
      this.playing = true;
      this.$emit('play');
      window.dispatchEvent(new CustomEvent('view-summary-play', { detail: this }));
    },
    onPause() {
      this.playing = false;
      window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this }));
    },
    ended() { this.onPause(); this.currentTime = 0; },
    updateTime() {
      const audio = this.$refs.audio;
      this.duration = Number.isFinite(audio.duration) ? audio.duration : this.clip.duration;
      this.currentTime = audio.currentTime;
    },
    onError() { this.playing = false; this.error = 'Audio is unavailable. Please try again.'; },
  },
};
</script>

<style scoped>
.welcome-audio { --welcome-icon-size: clamp(12px, 12cqw, 28px); --welcome-icon-gap: clamp(3px, 2.8cqw, 7px); position: relative; display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 0 clamp(3px, 2cqw, 6px); width: 100%; color: #373b35; font-family: 'Quattrocento Sans', sans-serif; }
.welcome-play { display: flex; align-items: center; gap: var(--welcome-icon-gap); min-height: 38px; padding: 0; border: 0; background: none; color: inherit; text-align: left; cursor: var(--site-interactive-cursor, pointer); transition: transform 160ms ease-out; }
.welcome-orbit { flex-shrink: 0; width: var(--welcome-icon-size); height: var(--welcome-icon-size); overflow: visible; }
.welcome-orbit-line { stroke: currentColor; stroke-width: .8; opacity: .35; transform-origin: 26px 26px; transition: transform 300ms cubic-bezier(.22,1,.36,1), opacity 180ms ease; }
.welcome-progress { stroke: currentColor; stroke-width: 1; transform: rotate(-90deg); transform-origin: center; }
.welcome-title { font: italic 500 clamp(9px, 8.7cqw, 22px)/1.2 'Cormorant Garamond', Georgia, serif; white-space: nowrap; transition: transform 220ms cubic-bezier(.22,1,.36,1); }
.welcome-languages { display: flex; flex-shrink: 0; gap: 2px; margin-left: auto; }
.welcome-languages button { min-width: clamp(14px, 12cqw, 26px); min-height: 38px; padding: 0 2px; border: 0; background: transparent; color: #77796f; font-family: inherit; font-size: clamp(8px, 7cqw, 10px); line-height: 1; cursor: var(--site-interactive-cursor, pointer); transition: color 160ms ease, transform 160ms ease-out; }
.welcome-languages button[aria-pressed='true'] { color: inherit; text-decoration: underline; text-underline-offset: 5px; text-decoration-thickness: 1px; }
.welcome-audio button:active { transform: scale(.97); }
.welcome-audio button:focus-visible { outline: 1px solid currentColor; outline-offset: 3px; }
.welcome-play:focus-visible * { transition: none; }
.welcome-status:empty { display: none; }
.welcome-status { grid-column: 1 / -1; margin: 4px 0 0; font-size: .65rem; line-height: 1.4; }
@media (hover: hover) and (pointer: fine) {
  .welcome-play:hover .welcome-orbit-line { transform: rotate(12deg); opacity: .7; }
  .welcome-play:hover .welcome-title { transform: translateX(2px); }
  .welcome-languages button:hover { color: #373b35; }
}
@media (hover: none), (pointer: coarse) {
  .welcome-play:active .welcome-orbit-line { transform: rotate(12deg); opacity: .7; }
  .welcome-play:active .welcome-title { transform: translateX(2px); }
  .welcome-languages button:active { color: #373b35; }
}
@media (prefers-reduced-motion: reduce) {
  .welcome-audio * { transition: none !important; }
  .welcome-play:hover .welcome-title { transform: none; }
}
</style>
